import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Alert,
  Grid,
  Divider,
  Switch,
  FormControlLabel,
  InputAdornment,
  Autocomplete
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AccessTime as AccessTimeIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import api from '../utils/api';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const ACCESS_TYPES = [
  { value: 'included', label: 'Included', description: 'Free access with plan' },
  { value: 'pay-per-use', label: 'Pay Per Use', description: 'Fixed fee per booking' },
  { value: 'hourly-limit', label: 'Hourly Limit', description: 'Limited hours with potential overage fees' },
  { value: 'usage-limit', label: 'Usage Limit', description: 'Limited number of uses' },
  { value: 'hybrid', label: 'Hybrid', description: 'Base fee + hourly charges' }
];

const FacilityAccessEditor = ({ facilityAccess = [], onChange, onValidate }) => {
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      console.log('🔍 FacilityAccessEditor: Starting to load data...');
      console.log('API Base URL:', api.defaults.baseURL);
      console.log('Auth token present:', !!localStorage.getItem('token'));
      
      // Test API connectivity first
      console.log('Testing API connectivity...');
      
      const facilitiesRes = await api.get('/facilities').catch(err => {
        console.error('Facilities API failed:', err);
        throw err;
      });
      
      console.log('✅ Facilities API response:', {
        status: facilitiesRes.status,
        dataLength: facilitiesRes.data.data?.length,
        data: facilitiesRes.data
      });

      const facilitiesData = facilitiesRes.data.data || [];

      setFacilities(facilitiesData);

      console.log(`✅ Successfully loaded ${facilitiesData.length} facilities`);
      
      if (facilitiesData.length === 0) {
        console.warn('⚠️  No facilities found! Database might not be seeded.');
      }
      
    } catch (error) {
      console.error('❌ Error loading facilities and member types:', error);
      
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        console.error('Response headers:', error.response.headers);
      } else if (error.request) {
        console.error('No response received:', error.request);
      } else {
        console.error('Request setup error:', error.message);
      }
      
      // Set empty arrays to prevent crashes
      setFacilities([]);
    } finally {
      setLoading(false);
    }
  };

  const validateAccessRule = (rule, index) => {
    const ruleErrors = {};

    if (!rule.facility) {
      ruleErrors.facility = 'Facility is required';
    }

    if (!rule.accessType) {
      ruleErrors.accessType = 'Access type is required';
    }

    // Validate pricing for pay-per-use and hybrid types
    if (['pay-per-use', 'hybrid'].includes(rule.accessType)) {
      if (rule.pricing?.pricePerHour < 0) {
        ruleErrors.pricePerHour = 'Price cannot be negative';
      }
      if (rule.pricing?.pricePerUse < 0) {
        ruleErrors.pricePerUse = 'Price cannot be negative';
      }
    }

    // Validate limits
    if (rule.limits) {
      Object.keys(rule.limits).forEach(key => {
        if (rule.limits[key] < 0) {
          ruleErrors[key] = 'Limit cannot be negative';
        }
      });
    }

    // Validate time slots
    if (rule.restrictions?.allowedTimeSlots) {
      rule.restrictions.allowedTimeSlots.forEach((slot, slotIndex) => {
        if (!slot.startTime || !slot.endTime) {
          ruleErrors[`timeSlot${slotIndex}`] = 'Start and end times are required';
        } else {
          const [startHour, startMin] = slot.startTime.split(':').map(Number);
          const [endHour, endMin] = slot.endTime.split(':').map(Number);
          if ((endHour * 60 + endMin) <= (startHour * 60 + startMin)) {
            ruleErrors[`timeSlot${slotIndex}`] = 'End time must be after start time';
          }
        }
      });
    }

    return ruleErrors;
  };

  const validateAllRules = useCallback(() => {
    const allErrors = {};
    facilityAccess.forEach((rule, index) => {
      const ruleErrors = validateAccessRule(rule, index);
      if (Object.keys(ruleErrors).length > 0) {
        allErrors[index] = ruleErrors;
      }
    });

    setErrors(allErrors);
    const hasErrors = Object.keys(allErrors).length > 0;

    if (onValidate) {
      onValidate(!hasErrors, allErrors);
    }

    return !hasErrors;
  }, [facilityAccess, onValidate]);

  useEffect(() => {
    validateAllRules();
  }, [facilityAccess, validateAllRules]);

  const addFacilityAccess = () => {
    const newAccess = {
      facility: '',
      accessType: 'included',
      pricing: {
        pricePerHour: 0,
        pricePerUse: 0,
        currency: 'TRY'
      },
      limits: {
        hoursPerDay: null,
        hoursPerWeek: null,
        hoursPerMonth: null,
        usesPerDay: null,
        usesPerWeek: null,
        usesPerMonth: null,
        maxAdvanceBookingDays: 30
      },
      restrictions: {
        allowedTimeSlots: [],
        blockedDays: [],
        requiresApproval: false
      },
      memberTypeOverrides: [],
      isActive: true,
      notes: ''
    };

    onChange([...facilityAccess, newAccess]);
    setExpanded(`panel-${facilityAccess.length}`);
  };

  const updateFacilityAccess = (index, updates) => {
    const updatedAccess = [...facilityAccess];
    updatedAccess[index] = { ...updatedAccess[index], ...updates };
    onChange(updatedAccess);
  };

  const removeFacilityAccess = (index) => {
    const updatedAccess = facilityAccess.filter((_, i) => i !== index);
    onChange(updatedAccess);
  };

  const addTimeSlot = (accessIndex) => {
    const newSlot = {
      startTime: '09:00',
      endTime: '17:00',
      daysOfWeek: [...DAYS_OF_WEEK],
      description: ''
    };

    const updatedAccess = [...facilityAccess];
    if (!updatedAccess[accessIndex].restrictions.allowedTimeSlots) {
      updatedAccess[accessIndex].restrictions.allowedTimeSlots = [];
    }
    updatedAccess[accessIndex].restrictions.allowedTimeSlots.push(newSlot);
    onChange(updatedAccess);
  };

  const updateTimeSlot = (accessIndex, slotIndex, updates) => {
    const updatedAccess = [...facilityAccess];
    updatedAccess[accessIndex].restrictions.allowedTimeSlots[slotIndex] = {
      ...updatedAccess[accessIndex].restrictions.allowedTimeSlots[slotIndex],
      ...updates
    };
    onChange(updatedAccess);
  };

  const removeTimeSlot = (accessIndex, slotIndex) => {
    const updatedAccess = [...facilityAccess];
    updatedAccess[accessIndex].restrictions.allowedTimeSlots = 
      updatedAccess[accessIndex].restrictions.allowedTimeSlots.filter((_, i) => i !== slotIndex);
    onChange(updatedAccess);
  };

  const getFacilityName = (facilityId) => {
    const facility = facilities.find(f => f._id === facilityId);
    return facility ? `${facility.name} (${facility.type})` : 'Unknown Facility';
  };

  const getAccessTypeInfo = (accessType) => {
    return ACCESS_TYPES.find(t => t.value === accessType) || ACCESS_TYPES[0];
  };

  const renderPricingSection = (access, accessIndex) => {
    if (!['pay-per-use', 'hybrid', 'hourly-limit'].includes(access.accessType)) {
      return null;
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <MoneyIcon sx={{ mr: 1 }} />
          Pricing Configuration
        </Typography>
        
        <Grid container spacing={2}>
          {(['pay-per-use', 'hybrid'].includes(access.accessType)) && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Price Per Use"
                type="number"
                value={access.pricing?.pricePerUse || 0}
                onChange={(e) => updateFacilityAccess(accessIndex, {
                  pricing: {
                    ...access.pricing,
                    pricePerUse: parseFloat(e.target.value) || 0
                  }
                })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₺</InputAdornment>,
                }}
                error={!!errors[accessIndex]?.pricePerUse}
                helperText={errors[accessIndex]?.pricePerUse}
              />
            </Grid>
          )}
          
          {(['hourly-limit', 'hybrid'].includes(access.accessType)) && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Price Per Hour"
                type="number"
                value={access.pricing?.pricePerHour || 0}
                onChange={(e) => updateFacilityAccess(accessIndex, {
                  pricing: {
                    ...access.pricing,
                    pricePerHour: parseFloat(e.target.value) || 0
                  }
                })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₺</InputAdornment>,
                }}
                error={!!errors[accessIndex]?.pricePerHour}
                helperText={errors[accessIndex]?.pricePerHour}
              />
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  const renderLimitsSection = (access, accessIndex) => {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <AccessTimeIcon sx={{ mr: 1 }} />
          Usage Limits
        </Typography>
        
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="Hours Per Day"
              type="number"
              value={access.limits?.hoursPerDay || ''}
              onChange={(e) => updateFacilityAccess(accessIndex, {
                limits: {
                  ...access.limits,
                  hoursPerDay: e.target.value ? parseInt(e.target.value) : null
                }
              })}
              placeholder="No limit"
            />
          </Grid>
          
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="Hours Per Week"
              type="number"
              value={access.limits?.hoursPerWeek || ''}
              onChange={(e) => updateFacilityAccess(accessIndex, {
                limits: {
                  ...access.limits,
                  hoursPerWeek: e.target.value ? parseInt(e.target.value) : null
                }
              })}
              placeholder="No limit"
            />
          </Grid>
          
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="Hours Per Month"
              type="number"
              value={access.limits?.hoursPerMonth || ''}
              onChange={(e) => updateFacilityAccess(accessIndex, {
                limits: {
                  ...access.limits,
                  hoursPerMonth: e.target.value ? parseInt(e.target.value) : null
                }
              })}
              placeholder="No limit"
            />
          </Grid>
          
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="Uses Per Day"
              type="number"
              value={access.limits?.usesPerDay || ''}
              onChange={(e) => updateFacilityAccess(accessIndex, {
                limits: {
                  ...access.limits,
                  usesPerDay: e.target.value ? parseInt(e.target.value) : null
                }
              })}
              placeholder="No limit"
            />
          </Grid>
          
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="Uses Per Week"
              type="number"
              value={access.limits?.usesPerWeek || ''}
              onChange={(e) => updateFacilityAccess(accessIndex, {
                limits: {
                  ...access.limits,
                  usesPerWeek: e.target.value ? parseInt(e.target.value) : null
                }
              })}
              placeholder="No limit"
            />
          </Grid>
          
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="Uses Per Month"
              type="number"
              value={access.limits?.usesPerMonth || ''}
              onChange={(e) => updateFacilityAccess(accessIndex, {
                limits: {
                  ...access.limits,
                  usesPerMonth: e.target.value ? parseInt(e.target.value) : null
                }
              })}
              placeholder="No limit"
            />
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Max Advance Booking Days"
              type="number"
              value={access.limits?.maxAdvanceBookingDays || 30}
              onChange={(e) => updateFacilityAccess(accessIndex, {
                limits: {
                  ...access.limits,
                  maxAdvanceBookingDays: parseInt(e.target.value) || 30
                }
              })}
            />
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderTimeRestrictionsSection = (access, accessIndex) => {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <ScheduleIcon sx={{ mr: 1 }} />
          Time Restrictions
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Blocked Days</Typography>
          <Autocomplete
            multiple
            options={DAYS_OF_WEEK}
            value={access.restrictions?.blockedDays || []}
            onChange={(event, newValue) => {
              updateFacilityAccess(accessIndex, {
                restrictions: {
                  ...access.restrictions,
                  blockedDays: newValue
                }
              });
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select days to block access"
                variant="outlined"
              />
            )}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">Allowed Time Slots</Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => addTimeSlot(accessIndex)}
            >
              Add Time Slot
            </Button>
          </Box>
          
          {access.restrictions?.allowedTimeSlots?.map((slot, slotIndex) => (
            <Card key={slotIndex} variant="outlined" sx={{ mb: 1, p: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    fullWidth
                    label="Start Time"
                    type="time"
                    value={slot.startTime || '09:00'}
                    onChange={(e) => updateTimeSlot(accessIndex, slotIndex, {
                      startTime: e.target.value
                    })}
                    slotProps={{
                      inputLabel: { shrink: true }
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 3 }}>
                  <TextField
                    fullWidth
                    label="End Time"
                    type="time"
                    value={slot.endTime || '17:00'}
                    onChange={(e) => updateTimeSlot(accessIndex, slotIndex, {
                      endTime: e.target.value
                    })}
                    slotProps={{
                      inputLabel: { shrink: true }
                    }}
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={DAYS_OF_WEEK}
                    value={slot.daysOfWeek || []}
                    onChange={(event, newValue) => {
                      updateTimeSlot(accessIndex, slotIndex, {
                        daysOfWeek: newValue
                      });
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          size="small"
                          label={option.slice(0, 3)}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Days"
                        placeholder="Select days"
                      />
                    )}
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 2 }}>
                  <IconButton
                    color="error"
                    onClick={() => removeTimeSlot(accessIndex, slotIndex)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
              
              {errors[accessIndex]?.[`timeSlot${slotIndex}`] && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {errors[accessIndex][`timeSlot${slotIndex}`]}
                </Alert>
              )}
            </Card>
          ))}
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={access.restrictions?.requiresApproval || false}
              onChange={(e) => updateFacilityAccess(accessIndex, {
                restrictions: {
                  ...access.restrictions,
                  requiresApproval: e.target.checked
                }
              })}
            />
          }
          label="Requires Admin Approval"
        />
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading facilities and member types...</Typography>
      </Box>
    );
  }

  if (facilities.length === 0 && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          No facilities found. Please ensure facilities are created before configuring plan access.
        </Alert>
        <Button 
          variant="contained" 
          onClick={loadData} 
          sx={{ mt: 2 }}
        >
          Retry Loading
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Facility Access Configuration</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={addFacilityAccess}
        >
          Add Facility
        </Button>
      </Box>

      {facilityAccess.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No facility access rules configured. Click "Add Facility" to get started.
        </Alert>
      )}

      {facilityAccess.map((access, accessIndex) => {
        const facilityName = getFacilityName(access.facility);
        const accessTypeInfo = getAccessTypeInfo(access.accessType);
        const hasErrors = errors[accessIndex] && Object.keys(errors[accessIndex]).length > 0;

        return (
          <Accordion
            key={accessIndex}
            expanded={expanded === `panel-${accessIndex}`}
            onChange={(event, isExpanded) => setExpanded(isExpanded ? `panel-${accessIndex}` : false)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mr: 2 }}>
                <Box>
                  <Typography variant="h6">
                    {facilityName}
                    {hasErrors && <Chip color="error" size="small" label="Errors" sx={{ ml: 1 }} />}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {accessTypeInfo.label} - {accessTypeInfo.description}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {!access.isActive && <Chip size="small" label="Inactive" />}
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFacilityAccess(accessIndex);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            </AccordionSummary>
            
            <AccordionDetails>
              {hasErrors && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Please fix the validation errors below.
                </Alert>
              )}
              
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth error={!!errors[accessIndex]?.facility}>
                    <InputLabel>Facility</InputLabel>
                    <Select
                      value={access.facility || ''}
                      onChange={(e) => updateFacilityAccess(accessIndex, { facility: e.target.value })}
                      label="Facility"
                    >
                      {facilities.map((facility) => (
                        <MenuItem key={facility._id} value={facility._id}>
                          {facility.name} ({facility.type})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth error={!!errors[accessIndex]?.accessType}>
                    <InputLabel>Access Type</InputLabel>
                    <Select
                      value={access.accessType || 'included'}
                      onChange={(e) => updateFacilityAccess(accessIndex, { accessType: e.target.value })}
                      label="Access Type"
                    >
                      {ACCESS_TYPES.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          <Box>
                            <Typography>{type.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {type.description}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {renderPricingSection(access, accessIndex)}
              {renderLimitsSection(access, accessIndex)}
              {renderTimeRestrictionsSection(access, accessIndex)}

              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={access.isActive !== false}
                      onChange={(e) => updateFacilityAccess(accessIndex, { isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
                
                <TextField
                  label="Notes"
                  multiline
                  rows={2}
                  value={access.notes || ''}
                  onChange={(e) => updateFacilityAccess(accessIndex, { notes: e.target.value })}
                  sx={{ minWidth: 300 }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default FacilityAccessEditor;